import type {User} from 'nutshell-core/users/users-types';

import {colors} from 'shells/colors/colors';
import {EnvelopeNewIcon} from 'shells/icon/svg/envelope-new-icon';
import {GlobeDomainRegularIcon} from 'shells/icon/svg/globe-domain-regular-icon';
import {GlobeRegularSearchIcon} from 'shells/icon/svg/globe-regular-search-icon';
import {MegaphoneRegularIcon} from 'shells/icon/svg/megaphone-regular-icon';
import {MobilePhoneRegularIcon} from 'shells/icon/svg/mobile-phone-regular-icon';
import {PeopleRegularIcon} from 'shells/icon/svg/people-regular-icon';
import {WorldRegularIcon} from 'shells/icon/svg/world-regular-icon';
import {WrenchRegularIcon} from 'shells/icon/svg/wrench-regular-icon';
import {SecondarySidebar} from 'shells/navigation/secondary-sidebar/secondary-sidebar';
import type {NavItem, SubmenuNavItem} from 'shells/navigation/types';

import {MarketingFooter} from './marketing-footer';

const CONTEXT_LANDING_PAGES = 'landing-pages';
const CONTEXT_CAMPAIGNS = 'campaigns';

export function getEmbeddedContext(pathname?: string | null) {
    if (
        pathname &&
        (pathname.includes('email-marketing/landing-pages') ||
            pathname.includes('marketing/landing-pages'))
    ) {
        return CONTEXT_LANDING_PAGES;
    }

    return CONTEXT_CAMPAIGNS;
}

export const DASHBOARD = {
    id: 'dashboard',
    label: 'Dashboard',
    // @ts-expect-error
    icon: () => <MegaphoneRegularIcon size={16} color={colors.white} />,
    to: '/marketing/dashboard',
} as const;

const LANDING_PAGES_SECTION_FOR_EMBEDDED: NavItem[] = [
    {
        id: 'landing-pages',
        label: 'Landing pages',
        to: '/marketing/landing-pages',
        // @ts-expect-error
        icon: () => <WorldRegularIcon size={16} color={colors.white} />,
        activeMatch: /\/marketing\/landing-pages(?!-settings|\/domains$).*/,
    },
    {
        id: 'domains',
        label: 'Domains',
        to: '/marketing/landing-pages/domains',
        // @ts-expect-error
        icon: () => <GlobeDomainRegularIcon size={16} color={colors.white} />,
        hasDivider: true,
    },
];

export const OUTREACH_SECTION_FOR_LIMITED_ACCESS_CAMPAIGN_USERS = {
    id: 'outreach',
    label: 'Email Marketing',
    to: '/marketing/email/summary',
    // @ts-expect-error
    icon: () => <EnvelopeNewIcon size={16} color={colors.white} />,
    submenu: [
        {
            id: 'summary',
            label: 'Summary',
            to: '/marketing/email/summary',
        },
        {
            id: 'broadcasts',
            label: 'Broadcasts',
            to: '/marketing/email/broadcasts',
        },
    ],
} as const;

export const SMS_SECTION = NutClientConfig.hasSmsCampaignsFeature
    ? {
          id: 'sms',
          label: 'SMS',
          // @ts-expect-error
          icon: () => <MobilePhoneRegularIcon size={16} color={colors.white} />,
          to: '/marketing/sms/broadcasts',
          submenu: [
              {
                  id: 'sms-broadcasts',
                  label: 'Broadcasts',
                  to: '/marketing/sms/broadcasts',
                  requires: ['canAccessFullCampaigns'],
              },
              {
                  id: 'sms-drip-sequences',
                  label: 'Drip sequences',
                  to: '/marketing/sms/drip-sequences',
              },
          ],
      }
    : {
          id: 'sms',
          label: 'SMS',
          // @ts-expect-error
          icon: () => <MobilePhoneRegularIcon size={16} color={colors.white} />,
          to: '/marketing/sms/broadcasts',
          requiresUpgrade: true,
      };

const AUDIENCES_SECTION = {
    hasDivider: true,
    id: 'audiences',
    label: 'Audiences',
    // @ts-expect-error
    icon: () => <PeopleRegularIcon size={16} color={colors.white} />,
    to: '/marketing/audiences',
} as const;

const DOMAINS_SECTION_LINK: SubmenuNavItem = {
    id: 'domains',
    label: 'Domains',
    to: '/setup/domains',
    isExternalLink: true,
};

const TRACKING_SNIPPET_SECTION_LINK: SubmenuNavItem = {
    id: 'trackingSnippet',
    label: 'Tracking snippet',
    to: '/setup/tracking-snippet',
    isExternalLink: true,
};

const getLandingPagesSettingsTab = () => {
    return {
        id: 'landingPagesSettings',
        label: 'Landing pages',
        to: '/marketing/landing-pages-settings',
        activeMatch: /\/marketing\/landing-pages-settings.*/,
    };
};

const getLandingPagesSettingsSection = () => {
    return {
        id: 'setting',
        label: 'Setup',
        // @ts-expect-error
        icon: () => <WrenchRegularIcon size={16} color={colors.white} />,
        to: '/marketing/landing-pages-settings',
        submenu: [getLandingPagesSettingsTab()],
    };
};

export const getSettingsSection = (isEmbedded?: boolean, hasSmsCampaigns?: boolean) => {
    return {
        id: 'setting',
        label: 'Setup',
        // @ts-expect-error
        icon: () => <WrenchRegularIcon size={16} color={colors.white} />,
        to: '/marketing/settings',
        submenu: [
            ...(isEmbedded
                ? [
                      {
                          id: 'domains',
                          label: 'Domains',
                          to: '/marketing/domains',
                      },
                  ]
                : []),
            ...(!isEmbedded ? [getLandingPagesSettingsTab()] : []),
            {
                id: 'emailmarketingVerifiedsenders',
                label: 'Sender addresses',
                to: '/marketing/sender-address',
            },
            ...(hasSmsCampaigns && (!isEmbedded || NutClientConfig.hasRcfxEmbedSmsCampaignsFF)
                ? [
                      {
                          id: 'SMS',
                          label: 'SMS',
                          to: '/marketing/sms-setup',
                          requiresUpgrade: !NutClientConfig.hasSmsCampaignsFeature,
                      },
                  ]
                : []),
            ...(isEmbedded ? [] : [DOMAINS_SECTION_LINK, TRACKING_SNIPPET_SECTION_LINK]),
        ],
    };
};

export function getEmailMarketingSection(canAccessSetup: boolean) {
    const outreachSection = {
        id: 'outreach',
        label: 'Email',
        // @ts-expect-error
        icon: () => <EnvelopeNewIcon size={16} color={colors.white} />,
        to: '/marketing/email/broadcasts',
        submenu: [
            {
                id: 'broadcasts',
                label: 'Broadcasts',
                to: '/marketing/email/broadcasts',
            },
            {
                id: 'newsletters',
                label: 'Newsletters',
                to: '/marketing/email/newsletters',
            },
            {
                id: 'drip-sequences',
                label: 'Drip sequences',
                to: '/marketing/email/drip-sequences',
            },
            {
                id: 'templates',
                label: 'Templates',
                to: '/marketing/email/templates',
            },
        ],
    } as const;

    if (canAccessSetup && NutClientConfig.hasPreferenceCenterFF) {
        // @ts-expect-error
        outreachSection.submenu.push({
            id: 'preference-center',
            label: 'Preference center',
            to: '/marketing/email/preference-center',
        });
    }

    return outreachSection;
}

export function getWebsiteSection(hasCompetitorIq: boolean) {
    const websiteSection = {
        id: 'website',
        label: 'Website',
        to: '/marketing/analytics',
        // @ts-expect-error
        icon: () => <WorldRegularIcon size={16} color={colors.white} />,
        submenu: [
            {
                id: 'analytics',
                label: 'Analytics',
                to: '/marketing/analytics',
            },
            {
                id: 'visitors',
                label: 'VisitorIQ',
                to: '/marketing/visitors',
            },
            {
                id: 'nutshellForms',
                label: 'Nutshell forms',
                to: '/marketing/forms',
            },
            {
                id: 'connectedForms',
                label: 'Connected forms',
                to: '/marketing/connected-forms',
            },
        ],
    } as const;

    if (hasCompetitorIq) {
        // @ts-expect-error
        websiteSection.submenu.push({
            id: 'competitorIq',
            label: 'CompetitorSpy',
            to: '/marketing/competitors',
        });
    }

    // @ts-expect-error
    websiteSection.submenu.push({
        id: 'landingPages',
        label: 'Landing pages',
        to: '/marketing/landing-pages',
        activeMatch: /\/marketing\/landing-pages(?!-settings).*/,
    });

    if (NutClientConfig.hasMoneyPagesFF && NutClientConfig.hasMarketingAutomations) {
        // @ts-expect-error
        websiteSection.submenu.push({
            id: 'marketingAutomations',
            label: 'Automations',
            to: '/marketing/automations',
        });
    }

    return websiteSection;
}

export function getMarketingServicesSection(isIcp: boolean) {
    const marketingServicesLinks = [];

    if (isIcp) {
        marketingServicesLinks.push({
            id: 'digitalMarketingServices',
            label: 'Marketing services',
            to: '/marketing/digital-marketing-services',
        });
    }

    marketingServicesLinks.push({
        id: 'seoScan',
        label: 'SEO scan',
        to: '/marketing/seo-scan',
    });

    if (isIcp) {
        marketingServicesLinks.push({
            id: 'marketingResources',
            label: 'Marketing resources',
            to: '/marketing/marketing-resources',
        });
    }

    const marketingServicesSection = {
        id: 'lead-generation',
        label: 'Lead generation',
        // @ts-expect-error
        icon: () => <GlobeRegularSearchIcon size={16} color={colors.white} />,
        to: '/marketing/lead-generation',
        submenu: marketingServicesLinks,
    } as const;

    if (isIcp) {
        marketingServicesLinks.push({
            id: 'crmAdvertising',
            label: 'CRM advertising',
            to: '/marketing/crm-advertising',
        });
    }

    return marketingServicesSection;
}

type Props = {
    isEmbedded?: boolean;
    user: User;
};

export function MarketingSidebar(props: Props) {
    const {user, isEmbedded} = props;

    const hasFullAccessToCampaigns = user.permissions
        ? user.permissions.canAccessFullCampaigns
        : false;

    const canAccessSetup = user.permissions.canAccessSetup;

    const outreachSection = getEmailMarketingSection(canAccessSetup);
    const smsSection = NutClientConfig.hasSMSCampaigns ? [SMS_SECTION] : [];

    if (isEmbedded) {
        const embeddedContext = getEmbeddedContext(window.location.pathname);
        if (embeddedContext === CONTEXT_LANDING_PAGES) {
            return (
                <SecondarySidebar
                    navItems={[
                        ...LANDING_PAGES_SECTION_FOR_EMBEDDED,
                        ...(canAccessSetup ? [getLandingPagesSettingsSection()] : []),
                    ]}
                    isEmbedded={true}
                />
            );
        }

        // Intentionally omitting the marketing services section for embedded
        return hasFullAccessToCampaigns ? (
            <SecondarySidebar
                navItems={[
                    // @ts-expect-error
                    outreachSection,
                    ...(NutClientConfig.hasRcfxEmbedSmsCampaignsFF ? smsSection : []),
                    AUDIENCES_SECTION,
                    ...(canAccessSetup
                        ? [getSettingsSection(true, NutClientConfig.hasSMSCampaigns)]
                        : []),
                ]}
                isEmbedded={true}
            />
        ) : (
            <SecondarySidebar
                // @ts-expect-error
                navItems={[OUTREACH_SECTION_FOR_LIMITED_ACCESS_CAMPAIGN_USERS]}
                isEmbedded={true}
            />
        );
    }
    const navItems = [
        DASHBOARD,
        outreachSection,
        ...smsSection,
        getWebsiteSection(NutClientConfig.hasCompetitorIq),
        getMarketingServicesSection(NutClientConfig.isIcp),
        AUDIENCES_SECTION,
    ];

    if (canAccessSetup) {
        // @ts-expect-error
        navItems.push(getSettingsSection(false, NutClientConfig.hasSMSCampaigns));
    }

    return (
        <>
            {hasFullAccessToCampaigns ? (
                // @ts-expect-error
                <SecondarySidebar navItems={navItems} footer={<MarketingFooter />} />
            ) : (
                // @ts-expect-error
                <SecondarySidebar navItems={[OUTREACH_SECTION_FOR_LIMITED_ACCESS_CAMPAIGN_USERS]} />
            )}
        </>
    );
}
